<template>
  <div class="relative">
    <!-- Notification Bell Button -->
    <button
      @click="toggleNotifications"
      class="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg transition-colors"
      :class="{ 'text-blue-600': showNotifications }"
    >
      <BellIcon class="h-6 w-6" />
      <!-- Notification Badge -->
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[20px] h-5"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Notifications Dropdown -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="showNotifications"
        class="absolute right-0 z-50 mt-2 w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        @click.stop
      >
        <!-- Header -->
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-gray-900">Notificaciones</h3>
            <div class="flex items-center space-x-2">
              <button
                v-if="unreadCount > 0"
                @click="markAllAsRead"
                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Marcar todas como leídas
              </button>
              <button
                @click="showNotifications = false"
                class="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <!-- Notifications List -->
        <div class="max-h-96 overflow-y-auto">
          <div v-if="loading" class="p-4 text-center">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-2">Cargando notificaciones...</p>
          </div>

          <div v-else-if="activities.length === 0" class="p-8 text-center">
            <BellIcon class="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p class="text-gray-500">No hay notificaciones</p>
          </div>

          <div v-else class="divide-y divide-gray-100">
            <div
              v-for="activity in activities"
              :key="activity.id"
              class="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              :class="{ 'bg-blue-50': !activity.isRead }"
              @click="markAsRead(activity)"
            >
              <div class="flex items-start space-x-3">
                <!-- Activity Icon -->
                <div class="flex-shrink-0">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center"
                    :class="getActivityIconClass(activity.action)"
                  >
                    <component :is="getActivityIcon(activity.action)" class="h-4 w-4" />
                  </div>
                </div>

                <!-- Activity Content -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900">
                    {{ activity.description }}
                  </p>
                  <div class="flex items-center mt-1 space-x-2">
                    <p class="text-xs text-gray-500">
                      {{ formatTimeAgo(activity.createdAt) }}
                    </p>
                    <span class="text-xs text-gray-300">•</span>
                    <p class="text-xs text-gray-500 capitalize">
                      {{ activity.entity }}
                    </p>
                    <span class="text-xs text-gray-300">•</span>
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" :class="getSourceBadgeClass(activity.source)">
                      {{ activity.source?.toUpperCase() || 'WEB' }}
                    </span>
                  </div>
                </div>

                <!-- Unread Indicator -->
                <div v-if="!activity.isRead" class="flex-shrink-0">
                  <div class="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <router-link
            to="/activities"
            class="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
            @click="showNotifications = false"
          >
            Ver todas las actividades
          </router-link>
        </div>
      </div>
    </Transition>

    <!-- Backdrop -->
    <div
      v-if="showNotifications"
      class="fixed inset-0 z-40"
      @click="showNotifications = false"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { BellIcon, XMarkIcon, PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/vue/24/outline'
import { activityService } from '@/services/activityService'
import type { Activity } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

const showNotifications = ref(false)
const loading = ref(false)
const activities = ref<Activity[]>([])
const unreadCount = ref(0)

let pollInterval: NodeJS.Timeout | null = null

const toggleNotifications = async () => {
  showNotifications.value = !showNotifications.value
  if (showNotifications.value) {
    await loadNotifications()
  }
}

const loadNotifications = async () => {
  try {
    loading.value = true
    const response = await activityService.getRecentActivities(5)
    activities.value = response.data
  } catch (error) {
    console.error('Error loading notifications:', error)
  } finally {
    loading.value = false
  }
}

const loadUnreadCount = async () => {
  try {
    const response = await activityService.getUnreadActivities()
    unreadCount.value = response.count
  } catch (error) {
    console.error('Error loading unread count:', error)
  }
}

const markAsRead = async (activity: Activity) => {
  if (!activity.isRead) {
    try {
      await activityService.markAsRead(activity.id)
      activity.isRead = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch (error) {
      console.error('Error marking activity as read:', error)
    }
  }
}

const markAllAsRead = async () => {
  try {
    await activityService.markAllAsRead()
    activities.value.forEach(activity => {
      activity.isRead = true
    })
    unreadCount.value = 0
  } catch (error) {
    console.error('Error marking all activities as read:', error)
  }
}

const getActivityIcon = (action: string) => {
  switch (action) {
    case 'created':
      return PlusIcon
    case 'updated':
      return PencilIcon
    case 'deleted':
      return TrashIcon
    case 'viewed':
      return EyeIcon
    default:
      return BellIcon
  }
}

const getActivityIconClass = (action: string) => {
  switch (action) {
    case 'created':
      return 'bg-green-100 text-green-600'
    case 'updated':
      return 'bg-blue-100 text-blue-600'
    case 'deleted':
      return 'bg-red-100 text-red-600'
    case 'viewed':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const getSourceBadgeClass = (source: string) => {
  switch (source) {
    case 'api':
      return 'bg-purple-100 text-purple-800'
    case 'web':
    default:
      return 'bg-indigo-100 text-indigo-800'
  }
}

const formatTimeAgo = (dateString: string) => {
  return formatDistanceToNow(new Date(dateString), {
    addSuffix: true,
    locale: es
  })
}

const startPolling = () => {
  // Poll for new notifications every 30 seconds
  pollInterval = setInterval(loadUnreadCount, 30000)
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

onMounted(() => {
  loadUnreadCount()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>